import React, {useState, useEffect} from 'react';
import {__} from 'js/tool/page/current';
import {setTextInput, validateRequired} from 'js/reusable/formHelper';
import {FormMutation, Validator, FormValues, Callback, ValueSetter} from 'js/reusable/types';
import {Map} from 'js/reusable/types/general';

/**
 *  useFormMutation API:
 *
 *  initialValues: I.Map()
 *      {
 *          value: 'initialValue',
 *          ?validator: customValidatorMethod,
 *          valueSetter: (setTextInput)valueSetterMethod,
 *          required: (true)/false,
 *          ignoreDidChangeValidation: (false)/true - this will validate entry even if it wasn't changed
 *      }
 *
 *  You can call submitCallback when you want to save your form, hook will send values and closeCallback to it if
 *  there are no validation errors.
 */

function useFormMutation<TValues = any, TErrors extends {[_: string]: any} = Map>(
	initialValues: FormValues<TValues>,
	submitCallback?: Callback<TValues>
): FormMutation<TValues, TErrors> {
	const [values, setValues] = useState(initialValues);
	const [didChange, setDidChange] = useState<any>({});
	const [generalError, setGeneralError] = useState(null);
	const [validationErrors, setValidationErrors] =
	 useState<TErrors>({} as TErrors);

	useEffect(() => {
		setValidationErrors(validateForm(true));
	}, [values]);

	function changeValue(name: string) {
		return function(newValue: React.BaseSyntheticEvent | any) {
			if (!newValue) {
				return;
			}

			if (newValue && newValue.persist) {
				newValue.persist(); // syntheticEvent will be omitted in 'setValues' of useState hook without this line.
			}

			setDidChange((curr: any) => (
				{
					...curr,
					[name]: true,
				}));

			const formEntry = initialValues[name];

			if (formEntry?.valueSetter) {
				const valueSetter = formEntry.valueSetter as ValueSetter;
				setValues(currValues =>
					valueSetter({newValue, values: currValues, name})
				);

				return;
			}
			setValues(currValues => setTextInput({newValue, values: currValues, name}));
		};
	}

	function handleSubmit(closeCallback?: () => void) {
		const currValidationErrors = validateForm();

		setValidationErrors(currValidationErrors);

		if (Object.keys(currValidationErrors).length === 0) {
			submitCallback?.(getValues(), closeCallback);
		}
	}

	function validateForm(isFromChange = false) {
		const validationErrors = Object.keys(initialValues).
			reduce((accu: TErrors, name: string): TErrors => {
				const formEntry = values[name];

				if (isFromChange && !didChange[name] && !formEntry?.ignoreDidChangeValidation) {
					return accu;
				}

				if (formEntry?.required !== false && !validateRequired(formEntry)) {
					return {
						...accu,
						[name]: __('fb:entry is required'),
					};
				}

				if (formEntry?.validator) {
					const validator = formEntry?.validator as Validator<TErrors>;
					const currValidationErrors = validator({
						formEntry,
						values,
						name,
						isFromChange,
						ignoreDidChangeValidation: formEntry.ignoreDidChangeValidation,
					});

					if (currValidationErrors) {
						return Object.assign(accu, currValidationErrors);
					}
				}

				return accu;
			}, {} as TErrors);

		setDidChange(Object.keys(validationErrors).reduce((acc, cur) => ({...acc, [cur]: true}), {}));

		return validationErrors;
	}

	function resetForm(persistValues = false) {
		setDidChange({});

		if (persistValues) {
			setValues((currValues: any) => {
				for (const field in initialValues) {
					initialValues[field].value = currValues[field]?.value ?
						currValues[field].value :
						initialValues[field].value;
				}

				return initialValues;
			});
		}

		setValues(initialValues);
	}

	function hideGeneralError() {
		setGeneralError(null);
	}

	function getValues() {
		const valueObject = {...values};

		return Object.keys(valueObject).reduce((curr, field: string) => (
			{
				...curr,
				[field]: valueObject[field].value ?? null,
			}), {});
	}

	function getRequired() {
		return Object.keys(values).reduce((acc, field) =>
			({...acc,
				[field]: values[field] ?
				[undefined, null, true].includes(values[field].required) as boolean :
					null,
			}), {});
	}

	function setExternalValidationErrors(errors: { [key: string]: string; } | TErrors) {
		const newErrors = Object.keys(errors).reduce((acc: any, err: any) =>
			(
				{
					...acc,
					[err]: errors[__(err)],
				}), {});
		setValidationErrors(currErrors => Object.assign(currErrors, newErrors));
	}

	function validateFormAndGetValidationErrors() {
		const validationErrors = validateForm();
		setValidationErrors(validationErrors);

		return validationErrors;
	}

	return {
		values:              getValues(),
		changeValue,
		validationErrors,
		handleSubmit,
		resetForm,
		generalError,
		setGeneralError,
		hideGeneralError,
		setValidationErrors: setExternalValidationErrors,
		validateForm:        validateFormAndGetValidationErrors,
		required:            getRequired(),
	};
}

export default useFormMutation;
